import React, { useState, useCallback } from 'react';
import api from '../../services/api';

/**
 * PersonnelManagementModule - Module de gestion du personnel
 * Vue complète avec présence, retards, productivité et CRUD
 * Utilisable dans tous les dashboards
 */

const PersonnelManagementModule = ({ 
  employes = [], 
  setEmployes, 
  departmentName = 'Tous départements',
  heureLimite = '08:30',
  deptId = null,
}) => {
  const [presenceDate, setPresenceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});

  // Helpers
  const isRetard = (arr) => arr && arr !== '—' && arr > heureLimite;
  
  const Pbar = ({ pct }) => {
    const c = pct >= 70 ? '#4caf50' : pct >= 40 ? '#ff9800' : '#ef5350';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: c, transition: 'width 0.3s' }} />
        </div>
        <span style={{ fontSize: 10, color: '#778', width: 32, textAlign: 'right' }}>{pct}%</span>
      </div>
    );
  };

  // Modals
  const openModal = useCallback((type, data = {}) => {
    setForm(data);
    setModal(type);
  }, []);

  const closeModal = useCallback(() => {
    setModal(null);
    setForm({});
  }, []);

  const saveEmploye = async () => {
    if (!form.prenom || !form.nom || !form.poste) return;

    const localEmploye = {
      id: form._isEdit ? form.id : Date.now(),
      av: ((form.prenom[0] || '') + (form.nom[0] || '')).toUpperCase(),
      prenom: form.prenom,
      nom: form.nom,
      email: form.email || '',
      password: form.password || '',
      poste: form.poste,
      dept: form.dept || departmentName,
      filiale: form.filiale || 'FHC Groupe',
      color: form.color || '#66bb6a',
      contrat: form.contrat || 'CDI',
      heureLimite: form.heureLimite || heureLimite,
      arrivee: form.arrivee || '—',
      present: form.present === 'true' || form.present === true,
      tachesQuot: parseInt(form.tachesQuot) || 0,
      tachesDone: parseInt(form.tachesDone) || 0,
      score: parseFloat(form.score) || 0,
      assignedTasks: form.assignedTasks || []
    };

    try {
      if (form._isEdit && form.id) {
        await api.employeesCRUD.update(form.id, {
          prenom: form.prenom,
          nom: form.nom,
          poste: form.poste,
          contrat: form.contrat || 'CDI',
          heure_limite: form.heureLimite || heureLimite,
          color: form.color || '#66bb6a',
          score_performance: parseFloat(form.score) || 0,
          ...(deptId && { departement_id: deptId }),
        });
        setEmployes(prev => prev.map(e => e.id === localEmploye.id ? { ...e, ...localEmploye } : e));
      } else {
        const response = await api.completeResources.createEmployeeWithUser({
          prenom: form.prenom,
          nom: form.nom,
          poste: form.poste,
          email: form.email || null,
          password: form.password || null,
          contrat: form.contrat || 'CDI',
          heure_limite: form.heureLimite || heureLimite,
          color: form.color || '#66bb6a',
          score_performance: 0,
          role: 'employe',
          ...(deptId && { departement_id: deptId }),
        });
        if (response.success && response.employee) {
          const newEmp = response.employee;
          const u = newEmp.user || {};
          setEmployes(prev => [...prev, {
            ...localEmploye,
            id: newEmp.id,
            user_id: u.id,
            av: ((form.prenom[0] || '') + (form.nom[0] || '')).toUpperCase(),
          }]);
          if (response.credentials) {
            alert(`Employé créé !\nEmail: ${response.credentials.email}\nMot de passe: ${response.credentials.password}`);
          }
        } else {
          setEmployes(prev => [...prev, localEmploye]);
        }
      }
    } catch (err) {
      // Fallback local si l'API échoue
      console.warn('API employee save failed, using local:', err);
      if (form._isEdit) {
        setEmployes(prev => prev.map(e => e.id === localEmploye.id ? { ...e, ...localEmploye } : e));
      } else {
        setEmployes(prev => [...prev, localEmploye]);
      }
    }
    closeModal();
  };

  const deleteEmploye = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet employé ?')) return;
    try {
      await api.employeesCRUD.delete(id);
    } catch (err) {
      console.warn('API employee delete failed, removing locally:', err);
    }
    setEmployes(prev => prev.filter(e => e.id !== id));
  };

  // Calculs
  const presents = employes.filter(e => e.present);
  const retards = employes.filter(e => e.present && isRetard(e.arrivee));
  const absents = employes.filter(e => !e.present);
  const tauxAccomp = employes.filter(e => e.tachesQuot > 0).length > 0
    ? Math.round(employes.filter(e => e.tachesQuot > 0).reduce((s, e) => s + Math.round((e.tachesDone / e.tachesQuot) * 100), 0) / employes.filter(e => e.tachesQuot > 0).length)
    : 0;

  const Field = ({ label, name, type = 'text', opts, required }) => (
    <div style={{ marginBottom: 16 }}>
      <label style={{
        fontSize: 11,
        color: '#889',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        display: 'block',
        marginBottom: 6,
        fontWeight: 600
      }}>
        {label} {required && <span style={{ color: '#ef8080' }}>*</span>}
      </label>
      {opts ? (
        <select
          style={{
            width: '100%',
            background: '#0d1b2a',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 8,
            padding: '11px 14px',
            color: '#fff',
            fontSize: 14,
            outline: 'none'
          }}
          value={form[name] || ''}
          onChange={e => setForm(f => ({ ...f, [name]: e.target.value }))}
        >
          <option value="">-- Sélectionner --</option>
          {opts.map(o => (
            <option key={o} value={o} style={{ background: '#0d1b2a' }}>{o}</option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          style={{
            width: '100%',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 8,
            padding: '11px 14px',
            color: '#fff',
            fontSize: 14,
            outline: 'none'
          }}
          value={form[name] || ''}
          onChange={e => setForm(f => ({ ...f, [name]: e.target.value }))}
        />
      )}
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 24,
        flexWrap: 'wrap',
        gap: 16
      }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 6 }}>
            👥 Personnel — {departmentName}
          </h2>
          <p style={{ fontSize: 13, color: '#667' }}>
            Heure limite : {heureLimite} · Suivi présence, retards, productivité
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: '#667' }}>Jour :</span>
            <input
              type="date"
              value={presenceDate}
              onChange={e => setPresenceDate(e.target.value)}
              style={{
                background: '#0d1b2a',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 8,
                padding: '8px 12px',
                color: '#fff',
                fontSize: 12,
                outline: 'none'
              }}
            />
          </div>
          <button
            onClick={() => openModal('employe', { present: 'true', color: '#66bb6a', tachesQuot: 0, tachesDone: 0, score: 0 })}
            style={{
              background: '#C9A84C',
              border: 'none',
              borderRadius: 8,
              padding: '10px 20px',
              color: '#000',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            + Nouveau collaborateur
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 16,
        marginBottom: 24
      }}>
        <div style={{
          background: '#111f30',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 14,
          padding: 20,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: '#4caf50'
          }} />
          <div style={{ fontSize: 24, marginBottom: 12 }}>✅</div>
          <div style={{
            fontFamily: 'Cormorant Garamond, serif',
            fontSize: 32,
            fontWeight: 700,
            color: '#4caf50',
            marginBottom: 6,
            lineHeight: 1
          }}>
            {presents.length}
          </div>
          <div style={{ fontSize: 12, color: '#aaa', marginBottom: 4 }}>Présents</div>
          <div style={{ fontSize: 10, color: '#667' }}>sur {employes.length} collaborateurs</div>
        </div>

        <div style={{
          background: '#111f30',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 14,
          padding: 20,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: '#ef5350'
          }} />
          <div style={{ fontSize: 24, marginBottom: 12 }}>⏰</div>
          <div style={{
            fontFamily: 'Cormorant Garamond, serif',
            fontSize: 32,
            fontWeight: 700,
            color: '#ef5350',
            marginBottom: 6,
            lineHeight: 1
          }}>
            {retards.length}
          </div>
          <div style={{ fontSize: 12, color: '#aaa', marginBottom: 4 }}>En retard (après {heureLimite})</div>
          <div style={{ fontSize: 10, color: '#667' }}>
            {retards.length > 0 ? retards.map(r => r.prenom).join(', ') : '✓ Aucun retard'}
          </div>
        </div>

        <div style={{
          background: '#111f30',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 14,
          padding: 20,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: '#ff9800'
          }} />
          <div style={{ fontSize: 24, marginBottom: 12 }}>🚫</div>
          <div style={{
            fontFamily: 'Cormorant Garamond, serif',
            fontSize: 32,
            fontWeight: 700,
            color: '#ff9800',
            marginBottom: 6,
            lineHeight: 1
          }}>
            {absents.length}
          </div>
          <div style={{ fontSize: 12, color: '#aaa', marginBottom: 4 }}>Absents</div>
          <div style={{ fontSize: 10, color: '#667' }}>Non pointés</div>
        </div>

        <div style={{
          background: '#111f30',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 14,
          padding: 20,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: '#C9A84C'
          }} />
          <div style={{ fontSize: 24, marginBottom: 12 }}>📊</div>
          <div style={{
            fontFamily: 'Cormorant Garamond, serif',
            fontSize: 32,
            fontWeight: 700,
            color: '#C9A84C',
            marginBottom: 6,
            lineHeight: 1
          }}>
            {tauxAccomp}%
          </div>
          <div style={{ fontSize: 12, color: '#aaa', marginBottom: 4 }}>Taux accomplissement</div>
          <div style={{ fontSize: 10, color: '#667' }}>
            {tauxAccomp >= 70 ? '↑ Bon niveau' : '↓ À améliorer'}
          </div>
        </div>
      </div>

      {/* Alerte retards */}
      {retards.length > 0 && (
        <div style={{
          background: 'rgba(239,83,80,0.08)',
          border: '1px solid rgba(239,83,80,0.25)',
          borderRadius: 10,
          padding: '12px 16px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 10
        }}>
          <span style={{ fontSize: 16 }}>⏰</span>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#ef8080' }}>Retards détectés ce jour</div>
            <div style={{ fontSize: 11, color: '#667', marginTop: 2 }}>
              {retards.map(r => `${r.prenom} ${r.nom} (${r.arrivee})`).join(' · ')}
            </div>
          </div>
        </div>
      )}

      {/* Tableau */}
      <div style={{
        background: '#111f30',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 14,
        padding: 20
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
          Collaborateurs & productivité
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <th style={{ textAlign: 'left', padding: '12px', fontSize: 11, color: '#667', textTransform: 'uppercase', fontWeight: 600 }}>Collaborateur</th>
                <th style={{ textAlign: 'left', padding: '12px', fontSize: 11, color: '#667', textTransform: 'uppercase', fontWeight: 600 }}>Poste</th>
                <th style={{ textAlign: 'left', padding: '12px', fontSize: 11, color: '#667', textTransform: 'uppercase', fontWeight: 600 }}>Filiale</th>
                <th style={{ textAlign: 'left', padding: '12px', fontSize: 11, color: '#667', textTransform: 'uppercase', fontWeight: 600 }}>Arrivée</th>
                <th style={{ textAlign: 'left', padding: '12px', fontSize: 11, color: '#667', textTransform: 'uppercase', fontWeight: 600 }}>Statut</th>
                <th style={{ textAlign: 'left', padding: '12px', fontSize: 11, color: '#667', textTransform: 'uppercase', fontWeight: 600 }}>Tâches</th>
                <th style={{ textAlign: 'left', padding: '12px', fontSize: 11, color: '#667', textTransform: 'uppercase', fontWeight: 600 }}>Accomplissement</th>
                <th style={{ textAlign: 'left', padding: '12px', fontSize: 11, color: '#667', textTransform: 'uppercase', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {employes.map(emp => {
                const retard = emp.present && isRetard(emp.arrivee);
                const pct = emp.tachesQuot > 0 ? Math.round((emp.tachesDone / emp.tachesQuot) * 100) : 0;
                return (
                  <tr key={emp.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: emp.color + '22',
                          color: emp.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11,
                          fontWeight: 700
                        }}>
                          {emp.av}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>
                          {emp.prenom} {emp.nom}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontSize: 12, color: '#aaa' }}>{emp.poste}</td>
                    <td style={{ padding: '12px', fontSize: 12, color: '#aaa' }}>{emp.filiale}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        color: !emp.present ? '#ef5350' : retard ? '#ffb74d' : '#66bb6a',
                        fontWeight: 600,
                        fontSize: 13
                      }}>
                        {emp.arrivee}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {!emp.present ? (
                        <span style={{
                          fontSize: 10,
                          padding: '4px 10px',
                          borderRadius: 999,
                          background: 'rgba(239,83,80,0.15)',
                          color: '#ef8080',
                          fontWeight: 600
                        }}>
                          Absent
                        </span>
                      ) : retard ? (
                        <span style={{
                          fontSize: 10,
                          padding: '4px 10px',
                          borderRadius: 999,
                          background: 'rgba(255,152,0,0.15)',
                          color: '#ffb74d',
                          fontWeight: 600
                        }}>
                          ⏰ Retard
                        </span>
                      ) : (
                        <span style={{
                          fontSize: 10,
                          padding: '4px 10px',
                          borderRadius: 999,
                          background: 'rgba(76,175,80,0.15)',
                          color: '#66bb6a',
                          fontWeight: 600
                        }}>
                          ✓ À l'heure
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px', fontSize: 12, color: '#aaa' }}>
                      {emp.tachesDone}/{emp.tachesQuot}
                    </td>
                    <td style={{ padding: '12px', minWidth: 120 }}>
                      {emp.tachesQuot > 0 ? <Pbar pct={pct} /> : '—'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => openModal('employe', { ...emp, present: String(emp.present), _isEdit: true })}
                          style={{
                            background: 'rgba(66,165,245,0.1)',
                            border: '1px solid rgba(66,165,245,0.3)',
                            borderRadius: 6,
                            padding: '4px 10px',
                            color: '#64b5f6',
                            fontSize: 11,
                            cursor: 'pointer'
                          }}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => deleteEmploye(emp.id)}
                          style={{
                            background: 'rgba(239,83,80,0.1)',
                            border: '1px solid rgba(239,83,80,0.3)',
                            borderRadius: 6,
                            padding: '4px 10px',
                            color: '#ef8080',
                            fontSize: 11,
                            cursor: 'pointer'
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {employes.length === 0 && (
                <tr>
                  <td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: '#667', fontSize: 13 }}>
                    Aucun employé dans ce département
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Employé */}
      {modal === 'employe' && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(4px)'
          }}
          onClick={closeModal}
        >
          <div
            style={{
              background: '#162535',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 16,
              padding: 32,
              minWidth: 500,
              maxWidth: 600,
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 24
            }}>
              <span style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>
                {form._isEdit ? '✏️ Modifier l\'employé' : '➕ Nouvel employé'}
              </span>
              <button
                onClick={closeModal}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#889',
                  fontSize: 26,
                  cursor: 'pointer',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Field label="Prénom" name="prenom" required />
              <Field label="Nom" name="nom" required />
            </div>
            <Field label="Poste" name="poste" required />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Field label="Email" name="email" type="email" />
              <Field label="Mot de passe" name="password" type="password" />
            </div>
            <Field
              label="Contrat"
              name="contrat"
              opts={['CDI', 'CDD', 'Stage', 'Freelance']}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Field label="Heure limite" name="heureLimite" type="time" />
              <Field label="Heure arrivée" name="arrivee" type="time" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Field label="Tâches quotidiennes" name="tachesQuot" type="number" />
              <Field label="Tâches complétées" name="tachesDone" type="number" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Field label="Score (0-10)" name="score" type="number" />
              <Field label="Présent" name="present" opts={['true', 'false']} />
            </div>
            <Field label="Couleur (hex)" name="color" type="color" />

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <button
                onClick={closeModal}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  padding: '10px 20px',
                  color: '#aaa',
                  fontSize: 13,
                  cursor: 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={saveEmploye}
                style={{
                  background: '#C9A84C',
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 24px',
                  color: '#000',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonnelManagementModule;
